<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter, RouterView } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Sidebar from '@/components/layout/Sidebar.vue'
import Topbar from '@/components/layout/Topbar.vue'
import PairingBanner from '@/components/layout/PairingBanner.vue'
import AddTaskDialog from '@/components/tasks/AddTaskDialog.vue'
import { useTheme } from '@/composables/useTheme'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { watchSystem } = useTheme()

// v3.19.38: 全局右下角悬浮添加按钮 (FAB) — 此前只挂在文件页 (v3.18.2
// 加的局部按钮), 用户在其他页面找不到入口 ("按钮一直没出来")。提升到
// 布局层: 仪表盘/任务/集群/设置/文件全部可见, 点开即添加任务。
const showGlobalAddDialog = ref(false)

// v3.13.39 fix: 子路由 (dashboard/tasks/…) 渲染于本布局的 <RouterView>,
// 不再经由 App 层的 slot —— 修复 MainLayout 被嵌套渲染两次的架构 bug。
const isBlank = computed(() => route.meta.layout === 'blank')
const ready = ref(false)

// v3.18.5 fix: App 层不再按 fullPath 重建 MainLayout (过渡卡死修复) 后,
// 本布局的 <main> 滚动容器跨页面持久 — 切页时旧滚动偏移残留到新页面。
// 路由变化后重置滚动位置 (router.scrollBehavior 只管 window, 管不到这里)。
const mainEl = ref<HTMLElement | null>(null)
watch(
  () => route.path,
  () => {
    void nextTick(() => {
      if (mainEl.value) mainEl.value.scrollTop = 0
    })
  },
)

// v3.13.81: 移动端抽屉导航 (<md; 桌面侧边栏不受影响)
const mobileOpen = ref(false)

watchSystem()

// 路由变化自动收起抽屉 (含 logout 跳转)
watch(
  () => route.path,
  () => {
    mobileOpen.value = false
  },
)

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') mobileOpen.value = false
}
onMounted(() => {
  ready.value = true
  window.addEventListener('keydown', onKeydown)
  void router
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div v-if="isBlank" class="min-h-screen bg-bg-base text-fg">
    <slot />
  </div>
  <div
    v-else
    class="relative flex h-screen w-screen overflow-hidden text-fg pt-[env(safe-area-inset-top)]"
  >
    <!-- v2: 光斑背景层, 让玻璃面板有内容可透 -->
    <div class="bg-orbs" aria-hidden="true"></div>
    <!-- 桌面侧边栏 (md+; v3.13.81: 手机上隐藏, 走抽屉) -->
    <div class="hidden md:block">
      <Sidebar />
    </div>
    <div class="relative z-10 flex min-w-0 flex-1 flex-col overflow-hidden">
      <Topbar @toggle-menu="mobileOpen = true" />
      <!-- v3.13.55: 待确认配对横幅 — 全局可见 (旧版只在集群设置页, 操作者错过批准窗口) -->
      <PairingBanner />
      <main
        id="main-content"
        ref="mainEl"
        tabindex="-1"
        role="main"
        aria-label="Main content"
        class="flex-1 overflow-auto focus:outline-none"
      >
        <div class="mx-auto w-full max-w-screen-2xl p-4 sm:p-6">
          <RouterView />
        </div>
      </main>
    </div>

    <!-- v3.13.81: 移动端抽屉 (遮罩 + 左滑入; 路由变化/Esc/点遮罩自动收起) -->
    <Transition name="drawer">
      <div v-if="mobileOpen" class="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
        <div
          class="drawer-backdrop absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          @click="mobileOpen = false"
        />
        <div class="drawer-panel absolute inset-y-0 left-0 w-64 max-w-[82vw] shadow-2xl">
          <Sidebar class="h-full" />
        </div>
      </div>
    </Transition>

    <!-- v3.19.38: 全局右下角悬浮添加按钮 — Teleport 到 body 脱离内容列的
         层叠上下文 (与文件页 v3.18.3 同款修法): 内容(10) < FAB(40) <
         抽屉(50) < Toast(60)。内联样式确保任何构建配置下都渲染。 -->
    <Teleport to="body">
      <button
        style="position:fixed;bottom:24px;right:24px;z-index:40;width:56px;height:56px;border-radius:50%;background:#6366f1;color:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(99,102,241,.4)"
        :title="t('tasks.add')"
        :aria-label="t('tasks.add')"
        @click="showGlobalAddDialog = true"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
    </Teleport>

    <!-- 添加任务弹窗 (全局 FAB 共用; 各页面自有的添加按钮继续独立工作) -->
    <AddTaskDialog v-model="showGlobalAddDialog" />
  </div>
</template>
